import { useState } from 'react'
import { Handshake, LayoutDashboard, Sparkles } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'

const LAYERS = [
  {
    id: 'base',
    icon: Handshake,
    art: '/home/what-we-are-partner.webp?v=white',
    wellClass: 'bg-amber-50',
    iconClass: 'text-amber-700',
    title: {
      en: 'Full-service merch partner',
      sv: 'Fullservice-merchpartner',
    },
    body: {
      en: 'Serving customers since 2015. From idea to delivery, a human on every order.',
      sv: 'Vi har hjälpt kunder sedan 2015. Från idé till leverans, en människa på varje order.',
    },
    detail: {
      en: 'We quote, print, proof, produce and deliver. A person at Klayder always reviews before you approve.',
      sv: 'Vi tar fram offert, tryck, korrektur, produktion och leverans. En person på Klayder granskar alltid innan ni godkänner.',
    },
  },
  {
    id: 'middle',
    icon: LayoutDashboard,
    art: '/home/what-we-are-workspace.webp?v=white',
    wellClass: 'bg-stone-100',
    iconClass: 'text-stone-600',
    title: {
      en: 'One place to manage merch',
      sv: 'Ett ställe att hantera merch',
    },
    body: {
      en: 'Quotes, proofs, invoices and delivery stay in one place. You always have a clear overview.',
      sv: 'Offerter, korrektur, fakturor och leverans ligger på ett ställe. Ni har alltid en tydlig översikt.',
    },
    detail: {
      en: 'Each order lives in a shared workspace. See status at a glance, and keep every proof and invoice with the request.',
      sv: 'Varje order lever i en gemensam arbetsyta. Se status på ett ögonkast, och behåll varje korrektur och faktura med förfrågan.',
    },
  },
  {
    id: 'peak',
    icon: Sparkles,
    art: '/home/what-we-are-sara.webp?v=white',
    wellClass: 'bg-violet-50',
    iconClass: 'text-violet-700',
    title: {
      en: 'Sara, your smart assistant',
      sv: 'Sara, er smarta assistent',
    },
    body: {
      en: 'She finds products, builds kits and makes mockups.',
      sv: 'Hon hittar produkter, bygger kit och tar fram mockups.',
    },
    detail: {
      en: 'Always optional. Brief the team instead, or use both. A specialist still checks the quote.',
      sv: 'Alltid valfritt. Briefa teamet i stället, eller använd båda. En specialist går ändå igenom offerten.',
    },
  },
] as const

const TEAM = [
  {
    name: 'Alfred',
    title: { en: 'Software development', sv: 'Mjukvaruutveckling' },
    image: '/home/team-alfred.png',
    figureClass: '-rotate-6 md:mt-10',
  },
  {
    name: 'Klara',
    title: { en: 'Sales', sv: 'Sälj' },
    image: '/home/team-klara.png',
    figureClass: 'rotate-2 md:mt-4',
  },
  {
    name: 'Rasmus',
    title: { en: 'Growth & Sales', sv: 'Tillväxt & sälj' },
    image: '/home/team-carmelo.png',
    figureClass: 'rotate-3 z-10',
  },
  {
    name: 'Nora',
    title: { en: 'Sales', sv: 'Sälj' },
    image: '/home/team-nora.png',
    figureClass: '-rotate-3 md:mt-6',
  },
  {
    name: 'Carmelo',
    title: { en: 'Sales', sv: 'Sälj' },
    image: '/home/team-rasmus.png',
    figureClass: '-rotate-2 md:mt-8',
  },
] as const

type LayerId = (typeof LAYERS)[number]['id']

export const WhatWeAreSection = () => {
  const { t } = useLocalization()
  const [activeId, setActiveId] = useState<LayerId>('base')
  const active = LAYERS.find((layer) => layer.id === activeId) ?? LAYERS[0]
  const ActiveIcon = active.icon

  return (
    <Column
      id="what-we-are"
      className="mx-auto w-full max-w-6xl scroll-mt-24 items-center gap-10 md:gap-14"
    >
      <Column className="max-w-2xl items-center gap-4 text-center">
        <h2 className="text-balance text-4xl font-normal tracking-tight text-neutral-900 md:text-5xl">
          {t('What we are.', 'Vad vi är.')}
        </h2>
        <p className="text-lg text-neutral-700 md:text-xl">
          {t(
            'A merch partner with a real team, and one place to manage every order. Extra help if you want it.',
            'En merchpartner med ett riktigt team, och ett ställe att hantera varje order. Extra hjälp om ni vill.'
          )}
        </p>
      </Column>

      <Row className="w-full !flex-col items-stretch gap-3 rounded-[32px] bg-[#f4f5f7] p-3 md:!flex-row md:gap-4 md:rounded-[40px] md:p-4">
        <Column className="w-full gap-2 md:w-[38%] md:min-w-[16rem]">
          {LAYERS.map((layer) => {
            const Icon = layer.icon
            const selected = layer.id === activeId
            return (
              <button
                key={layer.id}
                type="button"
                aria-pressed={selected}
                onClick={() => setActiveId(layer.id)}
                className={cn(
                  'flex w-full flex-1 items-center rounded-[22px] border px-4 py-4 text-left transition-all duration-200 md:px-5 md:py-5',
                  selected
                    ? 'border-stone-300 bg-white shadow-soft'
                    : 'border-transparent bg-white/70 hover:border-stone-200 hover:bg-white'
                )}
              >
                <Row className="items-center gap-3">
                  <Column
                    className={cn(
                      'size-10 shrink-0 items-center justify-center rounded-xl',
                      layer.wellClass
                    )}
                  >
                    <Icon
                      className={cn('size-5', layer.iconClass)}
                      strokeWidth={1.75}
                    />
                  </Column>
                  <h3 className="min-w-0 text-base font-normal tracking-tight text-neutral-900 md:text-lg">
                    {t(layer.title.en, layer.title.sv)}
                  </h3>
                </Row>
              </button>
            )
          })}
        </Column>

        <Column className="relative h-[22rem] min-w-0 flex-1 shrink-0 justify-center overflow-hidden rounded-[24px] bg-white px-6 py-8 shadow-soft md:h-[26rem] md:rounded-[28px] md:px-10 md:py-10">
          {LAYERS.map((layer) => (
            <img
              key={layer.id}
              src={layer.art}
              alt=""
              aria-hidden
              className={cn(
                'pointer-events-none absolute inset-0 h-full w-full object-cover object-[80%_center] transition-opacity duration-500',
                layer.id === activeId ? 'opacity-80' : 'opacity-0'
              )}
            />
          ))}
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white from-[18%] via-white/75 to-white/10"
            aria-hidden
          />
          <Column className="relative z-10 gap-5">
            <Column
              className={cn(
                'size-12 items-center justify-center rounded-2xl',
                active.wellClass
              )}
            >
              <ActiveIcon
                className={cn('size-6', active.iconClass)}
                strokeWidth={1.75}
              />
            </Column>
            <Column className="max-w-lg gap-3">
              <h3 className="text-2xl font-normal tracking-tight text-neutral-900 md:text-3xl">
                {t(active.title.en, active.title.sv)}
              </h3>
              <p className="text-lg text-neutral-700 md:text-xl">
                {t(active.body.en, active.body.sv)}
              </p>
              <p className="text-base text-neutral-600 md:text-lg">
                {t(active.detail.en, active.detail.sv)}
              </p>
            </Column>
          </Column>
        </Column>
      </Row>

      <Column
        id="meet-the-team"
        className="w-full scroll-mt-24 items-center gap-8 md:gap-10"
      >
        <Column className="max-w-2xl items-center gap-4 text-center">
          <h2 className="text-balance text-4xl font-normal tracking-tight text-neutral-900 md:text-5xl">
            {t('Meet the team.', 'Möt teamet.')}
          </h2>
          <p className="text-lg text-neutral-700 md:text-xl">
            {t(
              'The people who work your order.',
              'Människorna som tar hand om er order.'
            )}
          </p>
        </Column>

        <Row className="w-full items-end justify-center !flex-col gap-8 md:!flex-row md:gap-0">
          {TEAM.map((member) => (
            <Column
              key={member.name}
              className={cn(
                'w-full items-center gap-4 transition-transform duration-300 md:w-auto md:-mx-3 md:hover:-translate-y-2 md:hover:rotate-0',
                member.figureClass
              )}
            >
              <img
                src={member.image}
                alt={member.name}
                className="h-56 w-auto object-contain drop-shadow-md md:h-72"
              />
              <Column className="items-center gap-0.5 text-center">
                <p className="text-lg text-neutral-900">{member.name}</p>
                <p className="text-base text-neutral-500">
                  {t(member.title.en, member.title.sv)}
                </p>
              </Column>
            </Column>
          ))}
        </Row>
      </Column>
    </Column>
  )
}
